import { Component, signal, inject, OnInit, OnDestroy, HostListener, Signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { AuthHttpService } from '../../services/auth-http.service';
import { TokenExpirationService } from '../../services/token-expiration.service';
import { InactivityService } from '../../services/inactivity.service';
import { LoggerService } from '../../services/logger.service';
import { SessionExpirationModalComponent } from '../session-expiration-modal/session-expiration-modal';
import { InactivityModalComponent } from '../inactivity-modal/inactivity-modal';
import { APP_VERSION } from '../../constants/version';
import { LoadingBarComponent } from '../loading-bar/loading-bar';
import { PosNumberItem } from '../../types/numeros-pos.model';
import { PosStateService } from '../../services/pos-state.service';
import { GpsService } from '../../services/gps.service';
import { GpsStatus } from '../../types/gps.model';
import { AppFooterComponent } from '../app-footer/app-footer';


@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, SessionExpirationModalComponent, InactivityModalComponent, LoadingBarComponent, AppFooterComponent],
  templateUrl: './main-layout.html',
  styleUrls: ['./main-layout.scss']
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private authService = inject(AuthHttpService);
  private logger = inject(LoggerService);
  tokenService = inject(TokenExpirationService);
  inactivityService = inject(InactivityService);
  posState = inject(PosStateService);
  gpsService = inject(GpsService);

  userName = signal('');
  appVersion = signal(APP_VERSION);
  isLoggingOut = signal(false);
  logoutProgress = signal(0);

  // GPS — derivado del signal reactivo del servicio
  gpsStatusValue = computed<GpsStatus>(() => this.gpsService.status());
  gpsLabel = computed(() => {
    switch (this.gpsStatusValue()) {
      case 'active':     return 'GPS Activo';
      case 'requesting': return 'Obteniendo GPS...';
      case 'inactive':   return 'GPS Inactivo';
      case 'timeout':    return 'GPS Timeout';
      case 'denied':     return 'GPS Denegado';
      case 'error':      return 'GPS Error';
      default:           return 'GPS Desconectado';
    }
  });
  gpsCssModifier = computed(() => {
    switch (this.gpsStatusValue()) {
      case 'active':     return 'ok';
      case 'requesting': return 'loading';
      default:           return 'error';
    }
  });

  // POS dropdown — delegado al servicio de estado
  isPosDropdownOpen = signal(false);
  isUserMenuOpen    = signal(false);
  get selectedPos(): Signal<PosNumberItem | null> { return this.posState.selectedPos; }
  get posNumbers(): Signal<PosNumberItem[]> { return this.posState.posNumbers; }

  private progressInterval: ReturnType<typeof setInterval> | null = null;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.main-layout__pos-selector')) {
      this.isPosDropdownOpen.set(false);
    }
    if (!target.closest('.main-layout__user-menu')) {
      this.isUserMenuOpen.set(false);
    }
  }

  ngOnInit(): void {
    this.logger.log('MainLayoutComponent inicializado');

    // Intentar obtener el nombre de múltiples fuentes
    let name = localStorage.getItem('fullName');

    // Validar que no sea string "undefined" o vacío
    if (!name || name === 'undefined' || name === 'null' || name.trim() === '') {
      name = localStorage.getItem('username');
    }
    if (!name || name === 'undefined' || name === 'null' || name.trim() === '') {
      name = localStorage.getItem('adUsername');
    }

    this.userName.set(name && name.trim() ? name : 'Usuario');

    // Solo cargar POS si el usuario tiene assignedPOS
    // Para usuarios AD sin assignedPOS, no mostrar el dropdown de POS
    const rawPOS = localStorage.getItem('assignedPOS');
    if (rawPOS) {
      try {
        const parsed: PosNumberItem[] = JSON.parse(rawPOS);
        if (parsed && parsed.length > 0) {
          this.posState.init(parsed);
        } else {
          // Si el array está vacío, limpiar el estado
          this.posState.clear();
        }
      } catch {
        this.logger.log('Error al parsear assignedPOS desde localStorage');
        this.posState.clear();
      }
    } else {
      // No hay assignedPOS en localStorage, limpiar el estado del POS
      // para evitar mostrar datos de sesiones anteriores
      this.posState.clear();
    }

    this.tokenService.startMonitoring();
    this.inactivityService.startMonitoring();

    // Suscribir al evento de cambio de permiso de geolocalización del navegador.
    // Esto permite que el indicador de GPS reaccione en tiempo real cuando el
    // usuario bloquea o desbloquea el permiso desde la configuración del navegador.
    void this.gpsService.watchPermissionChanges();

    // Sincronizar el estado del GPS con el permiso real del navegador.
    // Se ejecuta en cada inicio de sesión para reflejar el estado actual
    // (el usuario puede haber cambiado el permiso entre sesiones).
    this.refreshGpsStatus();
  }

  ngOnDestroy(): void {
    this.tokenService.stopMonitoring();
    this.inactivityService.stopMonitoring();
    this.gpsService.unwatchPermissionChanges();
  }

  /**
   * Sincroniza el indicador de GPS con el permiso real del navegador.
   *
   * Flujo:
   * 1. Si ya hay coordenadas válidas en sesión → marcar como activo sin volver a pedir permiso.
   * 2. Si no hay coordenadas → consultar el estado del permiso sin mostrar el diálogo al usuario:
   *    - 'granted'  → solicitar posición silenciosamente y guardarla.
   *    - 'prompt'   → marcar como 'inactive' (el permiso aún no fue otorgado; se pedirá
   *                   cuando el usuario inicie una activación).
   *    - 'denied'   → marcar como 'denied' para que el indicador lo refleje.
   */
  private async refreshGpsStatus(): Promise<void> {
    // Caso 1: coordenadas ya en caché de sesión → estado activo garantizado
    const stored = this.gpsService.getStoredCoordinates();
    if (stored) {
      // validateAndStore detectará el caché y actualizará el signal a 'active'
      await this.gpsService.validateAndStore();
      return;
    }

    // Caso 2: sin caché → revisar el permiso actual sin disparar el diálogo del navegador
    const permissionState = await this.gpsService.checkPermission();

    if (permissionState.isPermanentlyDenied) {
      // El usuario denegó el GPS — el signal ya fue actualizado a 'denied' por checkPermission
      this.logger.log('[MainLayout] GPS: permiso denegado permanentemente');
      return;
    }

    if (permissionState.canRequest) {
      // Permiso en estado 'prompt' (no decidido aún) — marcar como inactivo.
      // No solicitamos aquí para no interrumpir al usuario con un diálogo en cada login;
      // la solicitud real ocurre al intentar iniciar una activación.
      this.logger.log('[MainLayout] GPS: permiso pendiente de solicitud');
      // validateAndStore internamente marca 'requesting' al intentar; usamos 'inactive'
      // para indicar que aún no se tiene ubicación sin disparar el diálogo.
      return;
    }

    // Permiso concedido pero sin caché → obtener posición silenciosamente
    this.logger.log('[MainLayout] GPS: permiso concedido, obteniendo coordenadas');
    await this.gpsService.validateAndStore();
  }

  goToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  togglePosDropdown(): void {
    this.isPosDropdownOpen.update(open => !open);
  }

  toggleUserMenu(): void {
    this.isUserMenuOpen.update(open => !open);
  }

  selectPos(pos: PosNumberItem): void {
    this.posState.select(pos);
    this.isPosDropdownOpen.set(false);
    this.logger.log(`POS seleccionado: ${pos.number}`);
  }

  goToProfile(): void {
    this.router.navigate(['/perfil']);
  }

  logout(): void {
    this.isLoggingOut.set(true);
    this.logoutProgress.set(0);
    let current = 0;
    this.progressInterval = setInterval(() => {
      current = current < 85 ? current + Math.random() * 15 : current;
      this.logoutProgress.set(Math.min(current, 85));
    }, 400);

    const sessionId = localStorage.getItem('sessionId');
    this.authService.logout(sessionId!).subscribe({
      next: (response) => {
        if (response.success) {
          if (this.progressInterval) clearInterval(this.progressInterval);
          this.logoutProgress.set(100);
          setTimeout(() => {
            this.posState.clear();
            localStorage.clear();
            this.router.navigate(['/login']);
          }, 400);
        }
      }
    });
  }
}
